import { Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { LocationService } from '../../../shared/services/location.service';
import { UNDERSCORE } from '../../../shared/utils/constant';
import {
	CalledVolume,
	EcallOffCardItemResponse,
	EcallOffCardResponse,
	GroupCalledVolume,
} from '../../models/ecall-off-model';
import { ECallOffService } from '../../services/ecall-off.service';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

import { CalledVolumeComponent } from './called-volume.component';

interface GroupCalledBatch {
	millDescription: string;
	millCodeGroup: EcallOffCardItemResponse[];
	grossWeight: number;
	netWeight: number;
}

@Component({
	selector: 'copo-ecall-off-card',
	templateUrl: './ecall-off-card.component.html',
	styleUrl: './ecall-off-card.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		PageHeaderComponent,
		TranslateModule,
		NgFor,
		MatIconModule,
		RouterLink,
		CalledVolumeComponent,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe
	]
})

export class EcallOffCardComponent {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;
	ecallData?: EcallOffCardResponse;
	isLoading = false;
	isOffline = false;
	isReqDateVisible = false;
	isFromToDateVisible = false;
	isBatchCallOff = false;
	showComments = false;
	totalRequestedQuantity = 0;
	groupCalledBatch: GroupCalledBatch[] = [];
	groupCalledVolume: GroupCalledVolume[] = [];

	constructor(
		private readonly eCallOffService: ECallOffService,
		private readonly route: ActivatedRoute,
		private readonly locationService: LocationService,
	) {
		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.getEcallDetails();
	}

	getEcallDetails() {
		const { id = '' } = this.route.snapshot.params;
		const supplierCallOffNumber = String(id);
		if (supplierCallOffNumber === '') {
			this.isLoading = false;
			return;
		}
		this.eCallOffService.getEcallsOffCard(supplierCallOffNumber).then(ecall => {
			this.ecallData = ecall;
			this.isBatchCallOff = this.ecallData.callOffType === 'B';
			const { items } = this.ecallData;
			this.totalRequestedQuantity = this.isBatchCallOff
				? this.getTotalGrossWeightBatches(items)
				: this.getTotalRequestedVolume(items);
			this.setCommentsVisibility();
			this.setRddMode();
			this.isLoading = false;
			this.groupCalledBatch = [];
			this.groupCalledVolume = [];
			if (this.isBatchCallOff) {
				const groupedKeys = items.reduce((group: Record<string, EcallOffCardItemResponse[]>, item) => {
					const key = item.mill || this.UNDERSCORE;
					if (!Object.hasOwn(group, key)) {
						group[key] = [];
					}
					group[key].push(item);
					return group;
				}, {});
				for (const key of Object.keys(groupedKeys)) {
					const [firstItem] = groupedKeys[key];
					this.groupCalledBatch.push({
						millDescription: firstItem.millDescription || key,
						millCodeGroup: groupedKeys[key],
						grossWeight: this.getTotalGrossWeightBatches(groupedKeys[key]),
						netWeight: this.getTotalNetWeightBatches(groupedKeys[key])
					});
				}
			} else {
				const groupedKeysVolume = items.reduce((group: Record<string, CalledVolume[]>, item) => {
					const key = item.mill || this.UNDERSCORE;
					if (!Object.hasOwn(group, key)) {
						group[key] = [];
					}
					group[key].push(this.mapItemToCalledVolume(item));
					return group;
				}, {});
				for (const key of Object.keys(groupedKeysVolume)) {
					const [firstVolume] = groupedKeysVolume[key];
					this.groupCalledVolume.push({
						millCode: groupedKeysVolume[key][0]?.millCode ?? '',
						millDescription: firstVolume.millDescription || key,
						millCodeGroup: groupedKeysVolume[key],
						requestedVolume: this.getTotalRequestedVolume(groupedKeysVolume[key])
					});
				}
			}
		}).catch((error: unknown) => {
			console.error('Error when getting ecall', error);
			this.isLoading = false;
		});
	}

	setCommentsVisibility() {
		const hasHeaderComment = Boolean(this.ecallData?.comment);
		const hasItemComments = Boolean(this.ecallData?.items.some(item => item.comment));
		this.showComments = hasHeaderComment || hasItemComments;
	}

	setRddMode() {
		this.isReqDateVisible = false;
		this.isFromToDateVisible = false;
		if (!this.ecallData) {
			return;
		}
		if (this.ecallData.requestedDateForCallOffType === 'BETWEEN'
			|| this.ecallData.requestedDateForCallOffType === 'ASAP'
		) {
			this.isReqDateVisible = false;
			this.isFromToDateVisible = true;
			return;
		}
		if (this.ecallData.requestedDateForCallOffType === 'EARLIEST'
			|| this.ecallData.requestedDateForCallOffType === 'LATEST') {
			this.isReqDateVisible = true;
			this.isFromToDateVisible = false;
			return;
		}
		if (this.ecallData.items.length === 0) {
			return;
		}
		const [datedObject] = this.ecallData.items;
		if (datedObject.requestedDeliveryDateTo === undefined
			&& datedObject.requestedDeliveryDateFrom === undefined) {
			this.isReqDateVisible = false;
			this.isFromToDateVisible = false;
		} else if ((datedObject.requestedDeliveryDateTo && !datedObject.requestedDeliveryDateFrom)
			|| (datedObject.requestedDeliveryDateFrom && !datedObject.requestedDeliveryDateTo)) {
			this.isReqDateVisible = true;
			this.isFromToDateVisible = false;
		} else {
			this.isReqDateVisible = false;
			this.isFromToDateVisible = true;
		}
	}

	goBack() {
		this.locationService.back();
	}

	getTotalRequestedVolume(items: { requestedVolume?: number }[]) {
		return items.reduce((sum, item) => sum + Number(item.requestedVolume || 0), 0);
	}

	getTotalGrossWeightBatches(items: EcallOffCardItemResponse[]) {
		return items.reduce((sum, item) => sum + Number(item.qtyBatchBrut || 0), 0);
	}

	getTotalNetWeightBatches(items: EcallOffCardItemResponse[]) {
		return items.reduce((sum, item) => sum + Number(item.qtyBatchNet || 0), 0);
	}

	formatCustomerOrderItemNumber(customerOrderItemNumber?: string) {
		if (!customerOrderItemNumber) {
			return '';
		}
		const trimmed = customerOrderItemNumber.replace(/^0+/, '');
		return trimmed === '' ? '0' : trimmed;
	}

	getBatchCustomerOrderDisplay(batch: EcallOffCardItemResponse) {
		if (!batch.customerOrderNumber) {
			return this.UNDERSCORE;
		}
		const item = this.formatCustomerOrderItemNumber(batch.customerOrderItemNumber);
		return item ? `${ batch.customerOrderNumber }/${ item }` : batch.customerOrderNumber;
	}

	private mapItemToCalledVolume(item: EcallOffCardItemResponse): CalledVolume {
		return {
			millCode: item.mill ?? '',
			millDescription: item.millDescription ?? item.mill,
			supplierOrderReference: item.supplierOrderNumber ?? '',
			supplierOrderItem: item.supplierOrderItemNumber ?? '',
			requestedVolume: item.requestedVolume ?? 0,
			requestedVolumeUnit: item.requestedVolumeUnit ?? '',
			customerOrderReference: item.customerOrderNumber,
			customerOrderItem: item.customerOrderItemNumber,
			requestedDeliveryDateFrom: item.requestedDeliveryDateFrom,
			requestedDeliveryDateTo: item.requestedDeliveryDateTo,
			unloadingReference: item.unloadingReference,
			shipTo: item.callOffShipTo,
			shipToDescription: item.callOffShipToDescription,
			originalShipTo: item.shipTo,
			originalShipToDescription: item.shipToDescription,
			customerArticleReference: item.customerArticleReference,
			comment: item.comment,
		};
	}
}
